import { motion } from 'framer-motion'
import { CheckCircle, Clock, Video, Globe } from 'lucide-react'
import { process } from '@/content/site'
import { CalendlyBtn } from '@/components/ui/calendly-btn'

const included = [
  { icon: Video, text: 'Virtual one-on-one sessions via Zoom' },
  { icon: Clock, text: 'Flexible scheduling across time zones' },
  { icon: Globe, text: 'Available to professionals worldwide' },
  { icon: CheckCircle, text: 'IFS-informed coaching framework' },
]

const faqs = [
  {
    q: 'How is coaching different from therapy?',
    a: "Coaching is forward-focused. We're not digging into your past — we're working with your inner system to create clarity and momentum toward what you want. Therapy tends to focus on healing. Coaching focuses on moving forward.",
  },
  {
    q: 'What is IFS coaching?',
    a: "Internal Family Systems (IFS) is a model that sees the mind as made up of different 'parts' — each with its own perspective, fears, and goals. IFS coaching helps those parts work together instead of pulling against each other.",
  },
  {
    q: 'How long does coaching take?',
    a: "Every client is different. Some find clarity and momentum in a few sessions. Others work with Douglas for several months as they navigate major transitions. We'll know more after our first conversation.",
  },
  {
    q: 'Is coaching right for me?',
    a: "If you're a professional who feels stuck, pulled in multiple directions, or sensing a gap between where you are and where you want to be — coaching may be exactly what's missing.",
  },
]

export default function ServicesPage() {
  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      {/* Page header */}
      <div className="bg-slate-900 pt-28 pb-16 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">
            One-on-One Coaching
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">
            A Structured Path to Clarity
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="text-slate-400 text-lg max-w-2xl">
            Most people seek a coach because something's stuck, shifting, or missing. This is the work that changes that.
          </motion.p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

        {/* What's included */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">What Coaching Includes</h2>
            <div className="space-y-4 mb-8">
              {included.map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-slate-700 dark:text-slate-300">{text}</span>
                </div>
              ))}
            </div>
            <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent">
              Book Your Free Consultation
            </CalendlyBtn>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="bg-slate-900 rounded-3xl p-8">
            <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-4">Who This Is For</p>
            <h3 className="text-xl font-bold text-white mb-4">Midlife professionals who feel stuck</h3>
            <p className="text-slate-400 leading-relaxed mb-5">
              You've built a life that looks good on paper. Career, relationships, milestones. Yet there's a gap — a sense of meaning or direction that's missing.
            </p>
            <p className="text-slate-400 leading-relaxed mb-5">
              You've tried thinking your way through it. You've read the books, maybe seen a therapist. But something still holds you back.
            </p>
            <p className="text-slate-300 leading-relaxed font-light italic">
              "That's where IFS coaching comes in — not as another quick fix, but as a structured path toward lasting clarity."
            </p>
          </motion.div>
        </div>

        {/* Process steps */}
        <div className="mb-20">
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {process.heading}
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="text-slate-500 dark:text-slate-400 mb-10">
            {process.subheading}
          </motion.p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {process.steps.map((step, i) => (
              <motion.div key={step.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-slate-50 dark:bg-slate-800 rounded-2xl p-6 border border-slate-100 dark:border-slate-700">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white font-bold text-sm mb-4">
                  {i + 1}
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{step.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{step.body}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-8">
            Frequently Asked Questions
          </motion.h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-6 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">{faq.q}</h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-2xl mx-auto text-center px-4">
          <h2 className="text-2xl font-bold text-white mb-3">Start with a free conversation</h2>
          <p className="text-slate-400 mb-8">No pitch. No pressure. Just a real talk about where you are and what might help.</p>
          <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent">
            Book Free Consultation
          </CalendlyBtn>
        </div>
      </div>
    </div>
  )
}
